// src/store/modules/auth.ts
import { Module } from 'vuex';
import api from '@/services/api';
import { AUTH_TOKEN_STORAGE_KEY, AUTH_USER_STORAGE_KEY } from '@/config/app';
import { clearLoginWelcomePending, markLoginWelcomePending } from '@/support/loginWelcome';

export const CASHIER_SITE_CHOICE_REQUIRED_KEY = 'suite241.cashierSiteChoiceRequired';

export interface User {
  id: number;
  name: string;
  username: string;
  email?: string | null;
  role: 'caissier' | 'gerant' | 'utilisateur' | 'support' | 'demo' | 'superadmin' | 'super_admin';
  permissions?: string[];
  must_change_password: boolean;
  is_active: boolean;
  shop_id?: number | null;
  shop?: { id: number; name?: string | null; code?: string | null } | null;
  telephone1?: string | null;
  telephone2?: string | null;
  birth_date?: string | null;
  gender?: string | null;
  profile_photo_url?: string | null;
  profile_photo_path?: string | null;
  created_at?: string | null;
  accessible_shops?: AccessibleShop[];
}

export interface AccessibleShop {
  id: number;
  name?: string | null;
  code?: string | null;
  is_primary?: boolean;
}

export interface AuthState {
  token: string | null;
  user: User | null;
}

const authModule: Module<AuthState, any> = {
  namespaced: true,

  // Le profil sauvegardé permet de restaurer l'interface au rechargement.
  // Ses rôles et permissions ne constituent pas une autorisation côté serveur.
  state: (): AuthState => {
    const savedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);

    return {
      // Token Sanctum
      token: localStorage.getItem(AUTH_TOKEN_STORAGE_KEY),
      // Informations de l'utilisateur connecté
      user: savedUser ? JSON.parse(savedUser) : null,
    };
  },

  getters: {
    token(state): string | null {
      return state.token;
    },
    isAuthenticated(state): boolean {
      return !!state.token && !!state.user;
    },
    currentUser(state): User | null {
      return state.user;
    },
    currentRole(state): string | null {
      return state.user ? state.user.role : null;
    },
  },

  mutations: {
    SET_AUTH(state, payload: { token: string; user: User }) {
      state.token = payload.token;
      state.user = payload.user;

      // Sauvegarde dans le localStorage
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, payload.token);
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(payload.user));
    },

    CLEAR_AUTH(state) {
      if (state.user) {
        clearLoginWelcomePending(state.user.id);
      }
      state.token = null;
      state.user = null;
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    },

    UPDATE_USER(state, user: User) {
      state.user = user;
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
    },

    SET_USER(state, user: User | null) {
      state.user = user;
      if (user) {
        localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(AUTH_USER_STORAGE_KEY);
      }
    },

  },

  actions: {
    async syncShopContextForUser(
      { dispatch, rootGetters },
      payload: { user: User | null; requireCashierChoice?: boolean; silent?: boolean }
    ) {
      const user = payload.user;
      const accessibleShops = Array.isArray(user?.accessible_shops)
        ? user?.accessible_shops?.filter((shop) => Number(shop?.id) > 0) || []
        : [];
      const currentShopId = Number(rootGetters['shop/currentShopId'] || 0) || null;
      const openSessionShopId = Number(rootGetters['cashRegister/currentSession']?.shop_id || 0) || null;
      const isCashier = user?.role === 'caissier';
      const canUseGlobalShopAccess = ['support', 'superadmin', 'super_admin'].includes(String(user?.role || ''));
      const canSwitchGlobalUserShop = user?.role === 'utilisateur'
        && !user?.shop_id
        && Array.isArray(user?.permissions)
        && user.permissions.includes('shops.switch');
      const currentShopIsAccessible = Boolean(
        currentShopId && accessibleShops.some((shop) => Number(shop.id) === currentShopId)
      );

      let selectedShopId: number | null = null;

      if (user?.role === 'utilisateur' && !user?.shop_id && !canSwitchGlobalUserShop) {
        selectedShopId = null;
      } else if (isCashier && accessibleShops.length > 1 && payload.requireCashierChoice) {
        window.localStorage.setItem(CASHIER_SITE_CHOICE_REQUIRED_KEY, '1');
        selectedShopId = Number(accessibleShops.find((shop) => shop.is_primary)?.id || accessibleShops[0].id);
      } else if (currentShopId && openSessionShopId === currentShopId) {
        selectedShopId = currentShopId;
      } else if (!payload.requireCashierChoice && currentShopIsAccessible) {
        selectedShopId = currentShopId;
      } else if (canUseGlobalShopAccess && accessibleShops.length > 0) {
        selectedShopId = Number(
          accessibleShops.find((shop) => shop.is_primary)?.id || accessibleShops[0].id
        );
      } else if (accessibleShops.length === 1) {
        window.localStorage.removeItem(CASHIER_SITE_CHOICE_REQUIRED_KEY);
        selectedShopId = Number(accessibleShops[0].id);
      } else if (isCashier && accessibleShops.length > 1) {
        selectedShopId = null;
      } else if (user?.shop_id) {
        selectedShopId = Number(user.shop_id);
      }

      if (selectedShopId) {
        if (currentShopId === selectedShopId) {
          await dispatch('shop/fetchInfo', payload.silent ? { silent: true } : undefined, { root: true });
        } else {
          await dispatch(
            'shop/changeSite',
            payload.silent ? { shopId: selectedShopId, silent: true } : selectedShopId,
            { root: true }
          );
        }
      } else {
        await dispatch('shop/clearShopContext', null, { root: true });
      }
    },

    /**
     * Login
     */
    async login({ commit, dispatch }, payload: { username: string; password: string }) {
      const response = await api.post('/auth/login', payload);
      const data = response.data.data;

      commit('SET_AUTH', {
        token: data.token,
        user: data.user,
      });

      await dispatch('runtime/load', { force: true }, { root: true });

      if (data.user.must_change_password) {
        return data;
      }

      await dispatch('syncShopContextForUser', {
        user: data.user,
        requireCashierChoice: true,
      });

      await dispatch('userSettings/load', null, { root: true });

      markLoginWelcomePending(data.user.id);

      return data;
    },

    async forgotPassword(_context, payload: { email: string }) {
      const response = await api.post('/auth/forgot-password', payload);

      return response.data;
    },

    async resetPassword(_context, payload: { email: string; token: string; password: string; password_confirmation: string }) {
      const response = await api.post('/auth/reset-password', payload);
      return response.data;
    },

    /**
     * fetchMe
     */
    async fetchMe({ commit, dispatch }) {
      const response = await api.get('/auth/me');
      const user = response.data.data;

      commit('UPDATE_USER', user);

      if (user.must_change_password) {
        return user;
      }

      await dispatch('syncShopContextForUser', { user });

      await dispatch('userSettings/load', null, { root: true });

      return user;
    },

    /**
     * changePassword
     */
    async changePassword(
      { commit, dispatch },
      payload: {
        current_password: string;
        new_password: string;
        new_password_confirmation: string;
      }
    ) {
      const response = await api.post('/auth/change-password', payload);
      const data = response.data.data;

      commit('SET_AUTH', {
        token: data.token,
        user: data.user,
      });

      try {
        await dispatch('syncShopContextForUser', {
          user: data.user,
          requireCashierChoice: true,
          silent: true,
        });
      } catch (error) {
        console.warn('Erreur synchronisation contexte après changement de mot de passe', error);
      }

      try {
        await dispatch('userSettings/load', { silent: true }, { root: true });
      } catch (error) {
        console.warn('Erreur chargement paramètres après changement de mot de passe', error);
      }

      return response.data;
    },

    async updateProfile({ commit }, payload: FormData) {
      const response = await api.post('/auth/profile', payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const user = response.data.data;

      commit('UPDATE_USER', user);

      return response.data;
    },

    /**
     * Logout
     */
    async logout({ commit, dispatch }) {
      try {
        await api.post('/auth/logout');
      } catch (e) {
        // même si l’API échoue, on nettoie tout en local
      }

      commit('CLEAR_AUTH');
      window.localStorage.removeItem(CASHIER_SITE_CHOICE_REQUIRED_KEY);

      // ✅ IMPORTANT : nettoyer le contexte shop pour éviter le shop d’un ancien user
      await dispatch('shop/clearShopContext', null, { root: true });
      await dispatch('cashRegister/clear', null, { root: true });
      await dispatch('userSettings/clear', null, { root: true });
    },

    /**
     * Recharger depuis localStorage (au refresh)
     */
    async LOAD_USER_FROM_LOCALSTORAGE({ commit, dispatch }) {
      const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      const savedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);

      // si pas de token ou pas de user -> on nettoie
      if (!token || !savedUser) {
        commit('CLEAR_AUTH');
        await dispatch('shop/clearShopContext', null, { root: true });
        await dispatch('userSettings/clear', null, { root: true });
        return;
      }

      const user = JSON.parse(savedUser);

      // on restaure auth correctement
      commit('SET_AUTH', { token, user });

      await dispatch('runtime/load', null, { root: true });

      if (user.must_change_password) {
        return;
      }

      await dispatch('syncShopContextForUser', { user });

      await dispatch('userSettings/load', null, { root: true });
    },
  },

};

export default authModule;
